<template>
  <BaseModal
    :value="dialogState"
    :modal-title="
      isCreateForm
        ? $tc('campaign.createAndEditDialog.createTitle', {
            courseName: activeCourse && activeCourse.name,
          })
        : $tc('campaign.createAndEditDialog.editTitle', {
            campaignName: activeCampaign && activeCampaign.name,
          })
    "
    @close="reset"
  >
    <template #modal>
      <div @submit.prevent="submit()">
        <div>
          <span class="sub-title" v-text="'Personnaliser de la campagne'" />
          <div class="input-fields">
            <BaseTextField
              v-model="form.name"
              autofocus
              :label="$t('campaign.name')"
              :error-messages="nameErrors"
              :has-error="!!nameErrors"
              @keyup.enter="submit()"
            />
            <base-autocomplete
              v-model="form.course"
              :items="activeCourses"
              :error-messages="courseErrors"
              :has-error="!!courseErrors"
              outlined
              item-text="name"
              item-value="id"
              :label="$t('utils.course')"
              :disabled="!isCreateForm"
            />
            <BaseTextArea
              v-model="form.description"
              :label="$t('campaign.description')"
            />
          </div>
        </div>
        <div>
          <span class="sub-title" v-text="'Paramétre de la campagne'" />
          <div class="input-fields">
            <v-switch
              v-model="form.uniqueAnswer"
              :label="$t('campaign.createAndEditDialog.onceAnswer')"
              inset
            />
            <v-switch
              v-model="form.createFolderForEach"
              :label="$t('campaign.createAndEditDialog.createfolderforEach')"
              inset
            />
            <v-switch
              v-model="form.isNotified"
              :label="$t('campaign.createAndEditDialog.notifyCollaborator')"
              inset
            />
            <BaseSelect
              v-if="form.createFolderForEach"
              id="assignedWorkflow"
              v-model="form.workflow"
              :loading="isFetchingWorkflow"
              :items="workflowItems"
              :placeholder="$t('campaign.createAndEditDialog.noWorkflow')"
              item-text="name"
              item-value="id"
              solo
            />
            <BaseSelect
              id="assignedWorkflow"
              v-model="form.assignedCollaborator"
              :loading="isFetchingCollaborators"
              :items="assignedCollaboratorsItems"
              item-text="user.email"
              item-value="user.id"
              solo
            />
          </div>
        </div>
        <div>
          <span class="sub-title" v-text="'Mail d’accompagnement'" />
          <div class="input-fields">
            <BaseMarkdownEditor
              v-model="form.defaultEmailContent"
              :label="$t('campaign.defaultEmailContent')"
              :with-link="false"
            />
          </div>
        </div>
        <div class="relaunch-container">
          <div class="d-flex flex-column title">
            <span class="sub-title" v-text="$t('course.autoReminder')" />
            <span class="text-line" v-text="$t('course.reminderText')" />
          </div>
          <div class="item-container">
            <div class="config-container">
              <v-switch
                v-model="auto_reminder"
                :label="$t('course.reminderLabel')"
                inset
                @change="auto_reminder = $event"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <BaseButton
        text
        class="mx-2 ml-auto"
        type="secondary"
        color="#707080"
        @click="reset"
      >
        {{ $t("utils.cancel") }}
      </BaseButton>
      <BaseButton type="primary" @click="submit()">
        {{ $t("utils.validate") }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import { mapState } from "vuex";

export default {
  name: "CampaignCreateAndEditDialog",
  mixins: [validationMixin],
  props: {
    dialogState: {
      type: Boolean,
    },
  },
  data() {
    return {
      form: {
        course: null,
        name: null,
        description: null,
        createFolderForEach: true,
        workflow: null,
        assignedCollaborator: null,
        uniqueAnswer: false,
        defaultEmailContent: "",
        isNotified: true,
      },
      isFetchingCollaborators: false,
      isFetchingWorkflow: true,
      auto_reminder: false,
    };
  },
  computed: {
    ...mapState({
      activeOrganization: (state) => state.organization.activeOrganization,
      activeCourse: (state) => state.course.course,
      activeCourses: (state) => state.course.activeCourses,
      activeCampaign: (state) => state.course.activeCampaign,
      workflows: (state) => state.workflow.workflows,
    }),
    isCreateForm() {
      return !this.activeCampaign;
    },
    assignedCollaboratorsItems() {
      if (!this.activeOrganization.collaborators) {
        return [];
      }
      return [
        {
          user: {
            id: null,
            email: this.$t("campaign.createAndEditDialog.noCollaborator"),
          },
        },
        ...this.activeOrganization.collaborators,
      ];
    },
    workflowItems() {
      return [
        {
          id: null,
          name: this.$t("campaign.createAndEditDialog.noWorkflow"),
        },
        ...this.workflows,
      ];
    },
    //#region Form errors
    nameErrors() {
      if (!this.$v.form.name.$dirty) {
        return "";
      }
      if (!this.$v.form.name.required) {
        return this.$t("campaign.createAndEditDialog.nameRequired");
      }
      if (!this.$v.form.name.maxLength) {
        return this.$t("campaign.createAndEditDialog.nameMaxLength");
      }
      return "";
    },

    courseErrors() {
      if (!this.$v.form.course.$dirty) {
        return "";
      }
      if (!this.$v.form.course.required) {
        return this.$t("campaign.createAndEditDialog.courseRequired");
      }
      return "";
    },
    //#endregion
  },
  validations: {
    form: {
      course: {
        required,
      },
      name: {
        required,
        maxLength: maxLength(255),
      },
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    reset() {
      this.form = {
        name: null,
        description: null,
        createFolderForEach: true,
        assignedCollaborator: null,
        uniqueAnswer: false,
      };
      this.$emit("close");
      this.$v.$reset();
    },
    async fetchOrganizationCourses() {
      this.$store.commit("course/SET_ACTIVE_COURSES", []);
      await this.$store.dispatch("course/fetchCourses", {
        organizationId: this.activeOrganization.id,
        archived: false,
      });
    },
    initialize() {
      this.fetchOrganizationCourses();
      this.isFetchingCollaborators = true;
      this.isFetchingWorkflow = true;
      this.auto_reminder = this.activeCampaign?.autoReminder;
      this.$store
        .dispatch("workflow/fetchWorkflows", this.activeOrganization.id)
        .then(() => (this.isFetchingWorkflow = false));
      this.$store
        .dispatch(
          "organization/fetchOrganizationCollaborators",
          this.activeOrganization.id
        )
        .finally(() => {
          this.isFetchingCollaborators = false;
          if (!this.isCreateForm) {
            if (this.activeCampaign.assign?.id) {
              this.form.assignedCollaborator = this.activeCampaign.assign?.id;
            }
          }
        });
      if (!this.isCreateForm) {
        this.form.defaultEmailContent = this.activeCampaign.defaultEmailContent;
        this.form.name = this.activeCampaign.name;
        this.form.course = this.activeCampaign.course.id;
        this.form.description = this.activeCampaign.description;
        this.form.createFolderForEach = this.activeCampaign.createFolder;
        this.form.uniqueAnswer = this.activeCampaign.once;
        this.form.isNotified = this.activeCampaign.isNotified;
        this.form.workflow = this.activeCampaign.workflow
          ? this.activeCampaign.workflow.id
          : null;
      }
    },
    async submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;
      if (this.isCreateForm) {
        await this.create();
      } else {
        await this.edit();
      }
      this.reset();
    },
    async create() {
      try {
        const payload = {
          name: this.form.name,
          course: this.form.course,
          description: this.form.description ? this.form.description : "",
          default_email_content: this.form.defaultEmailContent,
          create_folder: this.form.createFolderForEach,
          workflow: this.form.createFolderForEach ? this.form.workflow : null,
          assign: this.form.assignedCollaborator,
          once: this.form.uniqueAnswer,
          auto_reminder: this.auto_reminder,
          is_notified: this.form.isNotified,
        };
        const campaign = await this.$store.dispatch(
          "course/createOrganizationCampaign",
          {
            organizationId: this.$route.params.organizationId,
            payload,
          }
        );
        this.$store.dispatch("snackbar/active", {
          message: this.$t("campaign.createAndEditDialog.success"),
          type: "SUCCESS",
        });
        this.$router.push({
          name: "campaignDetail",
          params: {
            organizationId: this.$route.params.organizationId,
            campaignId: campaign.id,
          },
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async edit() {
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description ? this.form.description : "",
          default_email_content: this.form.defaultEmailContent,
          create_folder: this.form.createFolderForEach,
          workflow: this.form.createFolderForEach ? this.form.workflow : null,
          assign: this.form.assignedCollaborator,
          once: this.form.uniqueAnswer,
          auto_reminder: this.auto_reminder,
          is_notified: this.form.isNotified,
        };
        await this.$store.dispatch("course/updateOrganizationCourseCampaign", {
          organizationId: this.activeOrganization.id,
          campaignId: this.activeCampaign.id,
          payload,
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("campaign.createAndEditDialog.success"),
          type: "SUCCESS",
        });
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.modal_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 1;
  padding: 8px 0px 6px 0px;
}
.input_group {
  .label {
    font-size: 12px;
    line-height: 15px;
    margin-bottom: 1em;
    color: #707080;
  }
  ::v-deep {
    .v-input__control .v-input__slot {
      & > fieldset {
        border: 1px solid #b6bdff;
      }
    }
  }
}
.sub-title {
  font-family: "Inter";
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: #353542;
}
.input-fields {
  padding: 24px 24px 0 24px;
}
::v-deep .v-input--selection-controls {
  margin-top: 0px !important;
  padding-top: 0px !important;
}

.relaunch-container {
  padding-inline: 24px;
  .title {
    margin-top: 24px;
    gap: 10px;
    .sub-title {
      font-family: "Inter";
      font-weight: 600;
      font-size: 16px;
      line-height: 19px;
      color: #353542;
    }

    .text-line {
      font-family: Inter;
      font-size: 14px;
      font-weight: 400;
      line-height: 16.94px;
      text-align: left;
    }
  }
  .item-container {
    margin-top: 24px;

    .config-container {
      display: flex;
      align-items: center;
      width: 100%;
      gap: 24px;

      .config {
        width: 100%;
        padding: 16px;
        ::v-deep.label {
          font-family: Inter;
          font-size: 14px !important;
          font-weight: 400;
          line-height: 16.94px;
          text-align: left;
        }
      }
    }
  }
}
</style>
